/**
 * Blocks checkout / Buy Now while a cart mutation is still in flight.
 *
 * Removing a gift-triggering product kicks off a chain of cart writes: the
 * line removal itself, then ct-atc-flow.js reconciling the free gift against
 * the new subtotal. Between those writes the drawer still shows the old
 * subtotal and the checkout button stays live, so spam-clicking it during the
 * gap sends the cart to checkout with the gifts but without their trigger.
 *
 * Every mutating flow brackets its work with begin()/end(). While the counter
 * is above zero the checkout controls are dimmed and a capture-phase listener
 * swallows activation before it can reach GoKwik's inline onclick.
 *
 * The counter is the only state, so nested and overlapping mutations keep the
 * block in place until the last one settles. A watchdog clears a stuck counter
 * so an unbalanced begin() can never leave checkout permanently dead.
 */
(function () {
  'use strict';

  // Every control that can start a checkout from the cart. The native theme
  // buttons are commented out in the templates today and GoKwik renders the
  // live one, but they are listed so the guard keeps working if they return.
  var CHECKOUT_SELECTOR = [
    '.gokwik-checkout button',
    '.sr-headless-checkout',
    '.cart__checkout-button',
    '[name="checkout"]',
    '#checkout',
    '#CartDrawer-Checkout',
    '.cart__dynamic-checkout-buttons button',
  ].join(',');

  var WATCHDOG_MS = 10000;

  var pending = 0;
  var watchdog = null;

  function sync() {
    document.documentElement.classList.toggle('cart-busy', pending > 0);
  }

  function begin() {
    pending += 1;
    clearTimeout(watchdog);
    watchdog = setTimeout(function () {
      pending = 0;
      sync();
    }, WATCHDOG_MS);
    sync();
  }

  function end() {
    pending = Math.max(0, pending - 1);
    if (pending === 0) clearTimeout(watchdog);
    sync();
  }

  function isBusy() {
    return pending > 0;
  }

  function block(event) {
    if (pending === 0) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest(CHECKOUT_SELECTOR)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // Capture phase on document: GoKwik's handler is an inline onclick on the
  // button itself, so stopping here always beats it to the event.
  document.addEventListener('click', block, true);
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    block(event);
  }, true);

  window.CartBusy = { begin: begin, end: end, isBusy: isBusy };
})();
